'use client';

import {
  Box,
  Flex,
  Icon,
  IconButton,
  Skeleton,
  Drawer,
  Text,
  Portal,
} from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { RiMenu4Fill } from 'react-icons/ri';
import { CategoryProps } from '@/types/Category';
import { api } from '@/services/api';
import { MdClose, MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { FaTag } from 'react-icons/fa6';

const LeftMenuItem = ({
  category,
  link,
  childOpen = false,
}: {
  category: CategoryProps;
  link: string;
  childOpen?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Flex
      flexDir="column"
      align="start"
      w="100%"
      display={childOpen ? 'none' : 'flex'}
    >
      <Flex
        width="100%"
        justify="space-between"
        align="center"
        py="7px"
        px="12px"
        borderRadius="10px"
        transition="all 0.18s"
        pos="relative"
        overflow="hidden"
        _before={{
          content: '""',
          pos: 'absolute',
          left: '0',
          top: '18%',
          bottom: '18%',
          w: '3px',
          borderRadius: 'full',
          bg: '#00B0CA',
          opacity: '0',
          transition: 'opacity 0.18s',
        }}
        _hover={{
          bg: { base: 'rgba(0,176,202,0.07)', _dark: 'rgba(0,176,202,0.1)' },
          cursor: 'pointer',
          _before: { opacity: '1' },
        }}
        role="group"
      >
        <Link href={link} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            w="5px"
            h="5px"
            borderRadius="full"
            flexShrink={0}
            bg={{ base: 'rgba(0,0,0,0.18)', _dark: 'rgba(255,255,255,0.18)' }}
            transition="background 0.18s"
            _groupHover={{ bg: '#00B0CA' }}
          />
          <Text
            fontSize="13.5px"
            fontWeight="500"
            color={{ base: '#2C3E50', _dark: '#CBD5E0' }}
            transition="color 0.18s"
            _groupHover={{ color: '#00B0CA' }}
            truncate
          >
            {category.name}
          </Text>
        </Link>
        {category.children.length > 0 && (
          <Icon
            fontSize="17px"
            color={{ base: 'rgba(0,0,0,0.22)', _dark: 'rgba(255,255,255,0.22)' }}
            flexShrink={0}
            ml="4px"
            _hover={{ cursor: 'pointer', color: '#00B0CA' }}
            onClick={(e) => { e.preventDefault(); setExpanded((p) => !p); }}
            transform={expanded ? 'rotate(-180deg)' : 'rotate(0)'}
            transition="transform 0.28s cubic-bezier(0.4,0,0.2,1), color 0.18s"
          >
            <MdOutlineKeyboardArrowDown />
          </Icon>
        )}
      </Flex>

      {category.children.length > 0 && (
        <Flex
          flexDir="column"
          align="start"
          pl="16px"
          w="100%"
          borderLeft="1.5px solid"
          borderColor={{ base: 'rgba(0,176,202,0.15)', _dark: 'rgba(0,176,202,0.12)' }}
          ml="14px"
          maxH={expanded ? 'auto' : '0'}
          overflow="hidden"
          transition="max-height 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s"
          opacity={expanded ? 1 : 0}
        >
          {category.children.map((child) => (
            <LeftMenuItem
              key={child.id}
              category={child}
              link={`${link}/${child.slug}`}
              childOpen={!expanded}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

const LeftMenu = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [categories, setCategories] = useState<CategoryProps[]>([]);

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/category');
        setCategories(response.data);
      } catch {
        // silent
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      closeOnEscape={false}
      size="xs"
      placement="start"
    >
      <Drawer.Trigger asChild>
        <IconButton
          bg="transparent"
          border="1px solid transparent"
          _hover={{
            bg: 'rgba(0,176,202,0.07)',
            borderColor: 'rgba(0,176,202,0.2)',
          }}
          _active={{ bg: 'rgba(0,176,202,0.12)' }}
          borderRadius="10px"
          transition="all 0.2s"
          w={{ base: '36px', md: '38px' }}
          h={{ base: '36px', md: '38px' }}
        >
          <Icon
            w={{ base: '20px', md: '22px' }}
            h={{ base: '20px', md: '22px' }}
            color={{ base: '#0A1B2B', _dark: '#C8DCE8' }}
          >
            <RiMenu4Fill />
          </Icon>
        </IconButton>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            bg={{ base: '#FAFBFC', _dark: '#08131E' }}
            zIndex="30"
            borderRadius="0 20px 20px 0"
            boxShadow="8px 0 48px rgba(0,0,0,0.18)"
            left="0"
            top="0"
            bottom="0"
            h="100svh"
            borderRight="1px solid"
            borderColor={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.06)' }}
            pos="relative"
            overflow="hidden"
          >
            {/* Background glow */}
            <Box
              pos="absolute"
              top="-80px"
              left="-60px"
              w="220px"
              h="220px"
              borderRadius="full"
              bg="rgba(0,176,202,0.06)"
              style={{ filter: 'blur(55px)' }}
              pointerEvents="none"
            />

            <Drawer.Header
              pt="24px"
              pb="16px"
              px="20px"
              borderBottom="1px solid"
              borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.05)' }}
            >
              <Drawer.Title>
                <Box w="130px">
                  <Image
                    src='/ido-logo.png'
                    alt="Logo idO"
                    width={90}
                    height={50}
                    quality={100}
                  />
                </Box>
                <Flex align="center" gap="6px" mt="5px">
                  <Box w="5px" h="5px" borderRadius="full" bg="#00B0CA" flexShrink={0} />
                  <Text
                    fontSize="10px"
                    fontWeight="500"
                    color={{ base: 'rgba(0,0,0,0.35)', _dark: 'rgba(255,255,255,0.25)' }}
                    letterSpacing="0.05em"
                  >
                    Ver melhor, muda tudo
                  </Text>
                </Flex>
              </Drawer.Title>
            </Drawer.Header>

            <Drawer.Body
              pt="0"
              px="14px"
              overflowY="auto"
              css={{ '&::-webkit-scrollbar': { display: 'none' } }}
            >
              <Flex w="full" pt="18px" pb="28px" flexDir="column">
                {/* Section header */}
                <Flex align="center" gap="8px" mb="12px" px="4px">
                  <Flex
                    w="22px"
                    h="22px"
                    bg="rgba(0,176,202,0.1)"
                    borderRadius="7px"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Icon w="11px" h="11px" color="#00B0CA">
                      <FaTag />
                    </Icon>
                  </Flex>
                  <Text
                    fontSize="10px"
                    fontWeight="700"
                    color={{ base: 'rgba(0,0,0,0.32)', _dark: 'rgba(255,255,255,0.28)' }}
                    letterSpacing="0.13em"
                    textTransform="uppercase"
                  >
                    Categorias
                  </Text>
                  <Box flex="1" h="1px" bg={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.07)' }} />
                </Flex>

                {isLoading ? (
                  <Flex flexDir="column" gap="6px">
                    {[75, 55, 88, 62, 80, 50, 70].map((w, i) => (
                      <Skeleton
                        key={i}
                        w={`${w}%`}
                        h="32px"
                        borderRadius="10px"
                        bg={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.06)' }}
                      />
                    ))}
                  </Flex>
                ) : (
                  categories.map((cat) => (
                    <LeftMenuItem
                      key={cat.id}
                      category={cat}
                      link={`/categoria/${cat.slug}`}
                    />
                  ))
                )}
              </Flex>
            </Drawer.Body>

            <Drawer.Footer p="0">
              <Flex
                w="full"
                flexDir="column"
                borderTop="1px solid"
                borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.05)' }}
                px="16px"
                pt="14px"
                pb="18px"
                gap="10px"
              >
                <Flex align="center" justify="center" gap="6px">
                  <Box w="16px" h="1px" bg={{ base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.08)' }} />
                  <Text
                    fontSize="10px"
                    color={{ base: 'rgba(0,0,0,0.28)', _dark: 'rgba(255,255,255,0.22)' }}
                    letterSpacing="0.04em"
                  >
                    © {new Date().getFullYear()} idO
                  </Text>
                  <Box w="16px" h="1px" bg={{ base: 'rgba(0,0,0,0.1)', _dark: 'rgba(255,255,255,0.08)' }} />
                </Flex>
              </Flex>
            </Drawer.Footer>

            <Drawer.CloseTrigger asChild>
              <Box
                pos="absolute"
                top="18px"
                right="14px"
                w="28px"
                h="28px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.07)' }}
                cursor="pointer"
                transition="all 0.18s"
                _hover={{ bg: 'rgba(0,176,202,0.15)' }}
              >
                <Icon w="14px" h="14px" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
                  <MdClose />
                </Icon>
              </Box>
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default LeftMenu;
